<template>
  <div>
    <Card v-if="!user" bordered :title="$t('Eerst inloggen')">
      <p>{{ $t('Log eerst in vooraleer je de tickets kan boeken') }}</p>
      <Button danger type="button" fill @click="$router.push({ name: 'Login', query: { redirect: $route.fullPath } })" :disabled="disabled">{{ $t('Inloggen') }}</Button>
    </Card>
    <Alert v-else-if="$route.query['revoke-queue-invite']" type="info" :title="$t('Uitnodiging geweigerd')">{{ $t('Je plaats in de wachtlijst is doorgegeven aan de volgende.') }}</Alert>
    <div v-else-if="event.availableSeats && (!event.hasQueue || event.activeQueue)">
      <Card v-if="step === 1" :title="$t('Bestel je tickets')" fill>
        <Form @submit="submitTickets">
          <Card v-for="tariff in openTariffs" :key="tariff.nid">
            <h3>
              {{ tariff.title }}
              <Availability :simple="!showOccupation" :course="event" :tariff="tariff" small />
            </h3>
            <Input :label="$t('Aantal plaatsen')" type="number" min="0" v-model.number="tariff.amountSeats" :max="tariff.max" :help="tariff.currentMax !== Infinity ? `Je kan nog ${tariff.currentMax} plaatsen kiezen` : ''" />
            <Input v-if="tariff.availability_wheelchair" :label="$t('Aantal rolstoelen')" type="number" min="0" v-model.number="tariff.amountWheelchairs" :max="tariff.maxWheelchair" :help="tariff.currentMaxWheelchair !== Infinity ? `Je kan nog ${tariff.currentMaxWheelchair} rolstoelen kiezen` : ''" />
            <template #aside><span style="white-space: nowrap;">{{ (tariff.amountSeats + tariff.amountWheelchairs) || 0 }} x {{ tariff.price|money }} = {{ (((tariff.amountSeats + tariff.amountWheelchairs) || 0) * tariff.price)|money }}</span></template>
          </Card>
          <p>
            <strong>{{ $t('Aantal tickets') }}</strong><br/>
            {{ amountSeats }}
          </p>
          <p>
            <strong>{{ $t('Totaal bedrag') }}</strong><br/>
            {{ total|money }}
          </p>
          <Button type="submit" success fill :disabled="amountSeats === 0">{{ $t('Verder') }}</Button>
        </Form>
      </Card>
      <iframe v-else :src="embedURL" width="100%" height="600px" @load="handleIFrame" />
    </div>
    <div v-else-if="event.disableWaitingList">
      <Alert :title="$t('Alle stoelen zijn momenteel volzet')">

      </Alert>
    </div>
    <div v-else>
      <Alert :title="$t('Alle stoelen zijn momenteel gereserveerd')">
        Er is momenteel een wachtlijst, wil je je aan de wachtlijst toevoegen? Je krijgt een mail van zodra een plaats vrijkomt.
      </Alert>
      <Card :title="$t('Wachtlijst')">
        <Alert v-if="error" type="danger"><p>Er is iets misgegaan, probeer opnieuw.</p><Button @click="error = false">Opnieuw proberen</Button></Alert>
        <Alert v-if="queued" type="success">Je bent toegevoegd aan de wachtlijst, we houden je op de hoogte van zodra er plaatsen beschikbaar zijn.</Alert>
        <Form v-else @submit="addToQueue">
          <Input :label="$t('Hoeveel tickets heb je nodig?')" type="number" min="1" v-model.number="manualAmountSeats" :max="event.maxTicketsPerFamily || 5" />
          <Button type="submit" success fill>Voeg me toe aan de wachtlijst</Button>
        </Form>
      </Card>
    </div>
  </div>
</template>

<script>
import Alert from '../Core/Alert'
import Card from '../Core/Card'
import Input from '../Core/Input'
import Form from '../Core/Form'
import Button from '../Core/Button'
import api from '../../api'
import Availability from '../Tariff/Availability'
export default {
  name: 'TicketSale',
  data () {
    return {
      tariff: false,
      error: false,
      queued: false,
      step: 1,
      manualAmountSeats: 0
    }
  },
  components: {
    Availability,
    Button,
    Form,
    Input,
    Card,
    Alert
  },
  props: {
    event: {
      type: Object
    }
  },
  mounted () {
    window.addEventListener('message', this.handleMessage)
    if (this.event.alreadyInQueue) this.queued = true
    if (this.event.tariffs.length === 1) {
      this.tariff = this.event.tariffs[0].nid
    }
  },
  beforeDestroy () {
    window.removeEventListener('message', this.handleMessage)
  },
  computed: {
    club () {
      return this.$store.getters['club/club']
    },
    auth () {
      return this.$store.getters['user/auth']
    },
    embedURL () {
      const autoSelect = []
      for (const tariff of this.event.tariffs) {
        if (tariff.amountSeats) autoSelect.push(`${tariff.nid};${tariff.amountSeats}`)
        if (tariff.amountWheelchairs) autoSelect.push(`${tariff.nid}WC;${tariff.amountWheelchairs}`)
      }
      return `${this.$apiUrl}/ticket_sales?${this.club.nid}/${this.event.nid}/${this.auth}&mode=public&autoSelect[]=${autoSelect.join('&autoSelect[]=')}`
    },
    user () {
      return this.$store.getters['user/user']
    },
    tariffSelect () {
      return this.event.tariffs.filter(tariff => {
        return !tariff.blockedRegistrationMessage
      }).map(tariff => {
        return {
          value: tariff.nid,
          label: tariff.title
        }
      })
    },
    amountSeats () {
      return this.event.tariffs.reduce((previousValue, currentValue) => {
        return previousValue + (parseInt(currentValue.amountSeats || 0) + parseInt(currentValue.amountWheelchairs || 0))
      }, 0) || 0
    },
    total () {
      return this.event.tariffs.reduce((previousValue, currentValue) => {
        return previousValue + (parseInt(currentValue.amountSeats || 0) + parseInt(currentValue.amountWheelchairs || 0)) * currentValue.price
      }, 0) || 0
    },
    openTariffs () {
      return this.event.tariffs.filter(tariff => {
        return !tariff.blockedRegistrationMessage
      }).map(tariff => {
        tariff.max = Math.min(tariff.maxRemainingTickets || Infinity, tariff.availability, Math.min(this.event.maxTicketsPerFamily || Infinity, tariff.maxRemainingTickets || Infinity) - this.amountSeats + tariff.amountSeats)
        tariff.currentMax = Math.min((tariff.maxRemainingTickets || Infinity) - (tariff.amountSeats || 0) - (tariff.amountWheelchairs || 0), tariff.availability - (tariff.amountSeats || 0), (this.event.maxTicketsPerFamily || Infinity) - this.amountSeats)
        tariff.maxWheelchair = Math.min(tariff.maxRemainingTickets || Infinity, tariff.availability_wheelchair, Math.min(this.event.maxTicketsPerFamily || Infinity, tariff.maxRemainingTickets || Infinity) - this.amountSeats + (tariff.amountWheelchairs || 0))
        tariff.currentMaxWheelchair = Math.min((tariff.maxRemainingTickets || Infinity) - (tariff.amountSeats || 0) - (tariff.amountWheelchairs || 0), tariff.availability_wheelchair - (tariff.amountWheelchairs || 0), (this.event.maxTicketsPerFamily || Infinity) - this.amountSeats)
        return tariff
      })
    },
    showOccupation () {
      if (!this.$store.getters['club/showOccupation']) {
        return false
      }
      if (this.$store.getters['club/showOccupationFrom']) {
        return this.tariff.availability <= this.$store.getters['club/showOccupationFrom']
      }
      return this.$store.getters['club/showOccupation']
    }
  },
  methods: {
    handleMessage (event) {
      switch (event.data.type) {
        case 'LBCartConfirmed':
          this.confirmTickets(event.data)
          break
        case 'LBCartExpired':
          // Timer expired, remove from cart, clear the timer and go back to step 1
          this.step = 1
          this.tariff = false
          this.amountSeats = 0

          this.$store.commit('notice/addNotices', {
            title: 'Bestelling verlopen',
            message: 'Je bestelling is verlopen, probeer opnieuw.',
            type: 'warning'
          })
          break
        case 'LBToast':
          this.$store.commit('notice/addNotices', {
            title: event.data.title || false,
            message: event.data.text,
            type: event.data.style || false
          })
          break
        default:
          console.log(`No handler found for event ${event.data.type}`)
      }
    },
    handleIFrame () {
      const iframe = document.querySelector('iframe')
      const interval = setInterval(function () {
        if (!iframe) return false
        if (!iframe.contentDocument) return false
        if (iframe.height !== iframe.contentDocument.body.scrollHeight + 'px') {
          iframe.height = iframe.contentDocument.body.scrollHeight + 'px'
        }
      }, 350)
      setTimeout(function () {
        clearInterval(interval)
      }, 8000)
    },
    async submitTickets () {
      if (this.event.hideVenueLayout) {
        // Submit tickets without venue
        try {
          await this.$store.dispatch('cart/addItem', {
            event: this.event,
            seats: this.openTariffs.filter(openTariff => openTariff.amountSeats > 0).map(openTariff => {
              return {
                tariff: openTariff.nid,
                quantity: openTariff.amountSeats
              }
            })
          })

          await this.$router.push({ name: 'Cart' })
        } catch (error) {
          this.$store.commit('notice/addNotices', {
            title: 'Kon tickets niet bestellen',
            message: error.message,
            type: 'warning'
          }, { root: true })
        }
      } else {
        // Go to venue planner
        this.step++
      }
    },
    async confirmTickets (data) {
      // Check if any selectedSeats in data, if not, throw error that something has failed
      if (!Array.isArray(data.selectedSeats) || data.selectedSeats.length === 0) {
        return this.$store.commit('notice/addNotices', {
          title: 'Kon tickets niet bestellen',
          message: 'Er is iets misgegaan, gelieve opnieuw te proberen',
          type: 'warning'
        }, { root: true })
      }

      // Check if has anything else in cart, if so ask to either empty or skip adding this item
      if (this.$store.getters['cart/cart'].items.length > 0) {
        await this.$store.dispatch('cart/empty')
      }
      // Add tickets to cart
      await this.$store.dispatch('cart/addItem', {
        event: this.event,
        seats: Object.values(data.selectedSeats)
      })
      await this.$router.push({ name: 'Cart' })
    },
    async addToQueue () {
      try {
        await api.post('ticket_sales_add_to_queue', {
          event: this.event.nid,
          uid: this.user.uid,
          amountSeats: this.event.hasQueue ? this.manualAmountSeats : this.amountSeats
        })
        this.queued = true
      } catch (err) {
        this.error = err
      }
    }
  },
  watch: {
    step () {
      // if (this.step === 2) {
      //   // If has no remaining normal spots, check if wheelchair option is ticked
      //   this.step = 1
      //   this.event.hasQueue = 1
      //   this.manualAmountSeats = this.amountSeats
      // }
    }
  }
}
</script>
